import React, { useState } from 'react';
import { Article } from '../types/article';
import { updateArticle, deleteArticle } from '../api/articles';

interface ArticleItemProps {
  article: Article;
  categories: string[];
  showDragHandle: boolean;
  showCategorySelect: boolean;
  onArticleUpdate: (article: Article) => void;
  onArticleDelete: (id: number) => void;
}

const ArticleItem: React.FC<ArticleItemProps> = ({
  article,
  categories,
  showDragHandle,
  showCategorySelect,
  onArticleUpdate,
  onArticleDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(article.title);
  const [isSaving, setIsSaving] = useState(false);

  const handleCategoryChange = async (newCategory: string) => {
    if (newCategory === 'etc') return;
    try {
      const updated = await updateArticle(article.id, { category: newCategory });
      onArticleUpdate({ ...article, category: updated.category });
    } catch (error) {
      console.error('Failed to update category:', error);
      alert('카테고리 수정에 실패했습니다.');
    }
  };

  const handleSaveTitle = async () => {
    if (!editTitle.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    setIsSaving(true);
    try {
      const updated = await updateArticle(article.id, { title: editTitle.trim() });
      onArticleUpdate({ ...article, title: updated.title });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update title:', error);
      alert('제목 수정 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditTitle(article.title);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('정말로 이 기사를 삭제하시겠습니까?')) {
      return;
    }
    try {
      await deleteArticle(article.id);
      onArticleDelete(article.id);
    } catch (error) {
      console.error('Failed to delete article:', error);
      alert('기사 삭제에 실패했습니다.');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const date = article.date ? new Date(article.date).toLocaleDateString('ko-KR') : '-';
  const displayCategory = categories.includes(article.category) ? article.category : 'etc';

  return (
    <div className="article-item" data-id={article.id}>
      {showDragHandle && (
        <div className="drag-handle">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
          </svg>
        </div>
      )}
      <div className="article-content">
        <div className="article-item-header">
          <div className="article-item-id">
            ID: {article.id} (Seq: {article.sequence || '-'})
          </div>
          <div className="article-item-meta">
            {showCategorySelect && (
              <select
                className="category-select"
                value={displayCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            )}
            <span>{article.press || '-'}</span>
            <span>{date}</span>
            {article.url && (
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="article-link"
                onClick={(e) => e.stopPropagation()}
              >
                🔗 원문 보기
              </a>
            )}
          </div>
          <div className="article-item-actions">
            <button
              className="btn-mini btn-edit-start"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
            >
              제목 수정
            </button>
            <button
              className="btn-delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
            >
              삭제
            </button>
          </div>
        </div>
        <div className="title-row">
          {!isEditing ? (
            <div className="title-display">
              <div className="article-item-title">{article.title || '제목 없음'}</div>
            </div>
          ) : (
            <div className="title-edit-form">
              <input
                type="text"
                className="title-input"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={handleKeyPress}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
              <button
                className="btn-mini btn-save-title"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveTitle();
                }}
                disabled={isSaving}
              >
                {isSaving ? '...' : '저장'}
              </button>
              <button
                className="btn-mini btn-cancel-title"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelEdit();
                }}
              >
                취소
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArticleItem;
